import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

class CartMilestoneRewards extends HTMLElement {
  #observer = new MutationObserver(() => this.#queueSync());
  #syncQueued = false;
  #queuedCartItems = null;

  connectedCallback() {
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-reached', 'data-reward-type', 'data-gift-variant-id'],
    });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartLinesUpdate);
    this.#celebrateNewGoals();
    this.#queueSync();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartLinesUpdate);
  }

  #handleCartLinesUpdate = (event) => {
    const currentRewards = document.querySelector('cart-milestone-rewards');
    if (currentRewards instanceof CartMilestoneRewards) {
      currentRewards.#queueSync(event.detail?.resource?.items);
    }
  };

  #celebrateNewGoals() {
    const reachedLevels = new Set(
      [...this.querySelectorAll('[data-cart-milestone-reward][data-reached="true"]')].map(
        (reward) => reward.dataset.level
      )
    );
    const previousLevels = window.__cartMilestoneReachedLevels;
    window.__cartMilestoneReachedLevels = reachedLevels;

    // Establish the initial state without celebrating goals that were already reached before page load.
    if (!(previousLevels instanceof Set)) return;
    const newlyReached = [...reachedLevels].filter((level) => !previousLevels.has(level));
    if (
      !this.hasAttribute('confetti-enabled') ||
      !newlyReached.length ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const colors = ['#211a15', '#c6922f', '#f1c75b', '#18bca8', '#ffffff'];
    const burst = document.createElement('div');
    burst.className = 'cart-milestone-confetti';
    burst.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 48; index += 1) {
      const piece = document.createElement('span');
      const angle = (Math.PI * 2 * index) / 48 + Math.random() * 0.2;
      const distance = 150 + Math.random() * 320;
      piece.style.setProperty('--confetti-x', `${Math.cos(angle) * distance}px`);
      piece.style.setProperty('--confetti-y', `${Math.sin(angle) * distance + 190}px`);
      piece.style.setProperty('--confetti-rotate', `${Math.round(Math.random() * 900 - 450)}deg`);
      piece.style.setProperty('--confetti-delay', `${Math.round(Math.random() * 120)}ms`);
      piece.style.backgroundColor = colors[index % colors.length];
      burst.append(piece);
    }

    document.body.append(burst);
    burst.addEventListener('animationend', () => burst.remove(), { once: true });
  }

  #queueSync(cartItems) {
    if (Array.isArray(cartItems)) this.#queuedCartItems = cartItems;
    if (this.#syncQueued) return;
    this.#syncQueued = true;
    queueMicrotask(() => {
      this.#syncQueued = false;
      const queuedCartItems = this.#queuedCartItems;
      this.#queuedCartItems = null;
      this.syncGift(queuedCartItems);
    });
  }

  async syncGift(cartItems = null) {
    if (window.__cartMilestoneGiftSyncing) {
      window.__cartMilestoneGiftSyncPending = true;
      if (Array.isArray(cartItems)) window.__cartMilestoneGiftPendingItems = cartItems;
      return;
    }

    const unlockedGifts = [...this.querySelectorAll('[data-cart-milestone-reward]')]
      .filter(
        (reward) =>
          reward instanceof HTMLElement &&
          reward.dataset.reached === 'true' &&
          reward.dataset.rewardType === 'free_gift' &&
          Number(reward.dataset.giftVariantId) > 0
      )
      .sort((a, b) => Number(b.dataset.level) - Number(a.dataset.level));

    const desiredReward = unlockedGifts[0];
    const desiredVariantId = desiredReward instanceof HTMLElement ? Number(desiredReward.dataset.giftVariantId) : 0;
    const desiredLevel = desiredReward instanceof HTMLElement ? desiredReward.dataset.level : '';

    window.__cartMilestoneGiftSyncing = true;
    try {
      const sectionIds = [
        ...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((element) => element.dataset.sectionId)
            .filter(Boolean)
        ),
      ];
      let currentCartItems = cartItems;
      if (!Array.isArray(currentCartItems)) {
        const cartUrl = new URL(`${window.Shopify.routes.root}cart.js`, window.location.origin);
        cartUrl.searchParams.set('_milestone_sync', Date.now().toString());
        const cartResponse = await fetch(cartUrl, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();
        currentCartItems = cart.items;
      }

      const giftItems = currentCartItems.filter((item) => item.properties?._cart_milestone_gift);
      const bundleLinesById = new Map();
      currentCartItems
        .filter((item) => item.properties?._bundle_id && !item.properties?._bundle_free_gift)
        .forEach((item) => {
          const bundleId = String(item.properties._bundle_id);
          bundleLinesById.set(bundleId, [...(bundleLinesById.get(bundleId) || []), item]);
        });
      const retainedBundleGiftIds = new Set();
      const bundleGiftsToRemove = currentCartItems.filter((item) => {
        if (!item.properties?._bundle_free_gift) return false;
        const bundleId = String(item.properties?._bundle_id || '');
        const bundleLines = bundleLinesById.get(bundleId) || [];
        const usesLinkedPrimary = bundleLines.some((line) => String(line.properties?._bundle_schema) === '2');
        const hasQualifyingLine = usesLinkedPrimary
          ? bundleLines.some((line) => line.properties?._bundle_primary)
          : bundleLines.length > 0;
        const shouldRemove = !bundleId || !hasQualifyingLine || retainedBundleGiftIds.has(bundleId);
        if (!shouldRemove) retainedBundleGiftIds.add(bundleId);
        return shouldRemove;
      });
      let cartItemCount = currentCartItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
      const desiredGift = giftItems.find(
        (item) => item.variant_id === desiredVariantId && String(item.properties._cart_milestone_gift) === desiredLevel
      );
      const giftsToRemove = giftItems.filter((item) => item !== desiredGift);
      const updates = Object.fromEntries(
        [...giftsToRemove, ...bundleGiftsToRemove].map((item) => [item.key, 0])
      );
      if (desiredGift && desiredGift.quantity !== 1) updates[desiredGift.key] = 1;

      if (Object.keys(updates).length) {
        const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        if (!updateResponse.ok) throw new Error('Could not update milestone gifts.');
        const updatedCart = await updateResponse.json();
        cartItemCount = Number(updatedCart.item_count || 0);
      }

      if (desiredVariantId && !desiredGift) {
        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: desiredVariantId,
                quantity: 1,
                properties: { _cart_milestone_gift: desiredLevel, _free_gift: 'true' },
              },
            ],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        if (!addResponse.ok) throw new Error('Could not add the milestone gift.');
        const addedItems = await addResponse.json();
        cartItemCount += 1;
      }

      if (Object.keys(updates).length || (desiredVariantId && !desiredGift)) {
        await Promise.all(
          sectionIds.map((sectionId) => sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' }))
        );
        document.querySelectorAll('cart-icon').forEach((cartIcon) => cartIcon.renderCartBubble?.(cartItemCount, false));
      }
    } catch (error) {
      console.warn('[cart-milestone-rewards] Gift sync failed:', error);
    } finally {
      window.__cartMilestoneGiftSyncing = false;
      if (window.__cartMilestoneGiftSyncPending) {
        window.__cartMilestoneGiftSyncPending = false;
        const pendingCartItems = window.__cartMilestoneGiftPendingItems;
        window.__cartMilestoneGiftPendingItems = null;
        const currentRewards = document.querySelector('cart-milestone-rewards');
        if (currentRewards instanceof CartMilestoneRewards) {
          queueMicrotask(() => currentRewards.syncGift(pendingCartItems));
        }
      }
    }
  }
}

if (!customElements.get('cart-milestone-rewards')) {
  customElements.define('cart-milestone-rewards', CartMilestoneRewards);
}
